import React, { Dispatch, SetStateAction } from 'react';
import { Dropdown } from 'react-bootstrap';
import { format } from 'date-fns';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { Settings } from '../../models/settings.model';
import { Tax } from '../../models/tax.model';
import { Names } from '../../models/names.model';
import { legendValid } from '../../utils/legend.data.util';
import { prefixLegend, suffixLegend } from '../../scripts/name.legend.script';
import { taxFeeSelected } from '../../scripts/list.selected.script';
import { taxFeeList } from '../../scripts/list.filter.script';

import AppLegend from './Legend';

export interface AppInputTaxFeeProps {
  component?: string,
  options: {main_modal: string | null, index: number | string | null, sub_index: number | string | null, type: 'sale' | 'purchase'},
  companyForUser: Company,
  settingsForCompany: Settings,
  taxCode?: {value: string, valid: boolean},
  taxRate?: {value: number, valid: boolean},
  variant?: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}},
  setTaxCode?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setTaxRate?: Dispatch<SetStateAction<{value: number, valid: boolean}>>,
  setVariant?: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}}>>
};

const AppInputTaxFee: React.FunctionComponent<AppInputTaxFeeProps> = ({component, companyForUser, settingsForCompany, options, taxCode, taxRate, variant, setTaxCode, setTaxRate, setVariant}) => {
  const {lang} = StateLanguage()

  const handleChangeTax = (item: Tax) => {
    if (taxCode && taxRate && setTaxCode && setTaxRate) {
      setTaxCode({value: item.code, valid: true})
      setTaxRate({value: item.rate, valid: true})

      legendValid(`${prefixLegend(component)}legend-tax-${options.type}-required${suffixLegend(options.index, options.sub_index)}`)
    }
  }

  const handleChangeTaxToVariant = (item: Tax) => {
    if (variant && setVariant) {
      if (options.type === 'sale') {
        setVariant({value: {...variant.value, tax_code_sale: item.code, tax_rate_sale: item.rate}, valid: {...variant.valid, tax_sale: true}})
      } else {
        setVariant({value: {...variant.value, tax_code_purchase: item.code, tax_rate_purchase: item.rate}, valid: {...variant.valid, tax_purchase: true}})
      }

      legendValid(`${prefixLegend(component)}legend-variant-tax-${options.type}-required${suffixLegend(options.index, options.sub_index)}`)
    }
  }

  function type(): string {
    return (options.type === 'sale') ? 'credit' : 'debit'
  }

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
          <span className="selection">
            <span className="select2-selection select2-selection--single form-select bg-light">
              <span className="select2-selection__rendered" role="textbox">
                { taxCode && taxRate && setTaxCode && setTaxRate &&
                  <>
                    {taxCode.valid && taxRate.valid ? taxFeeSelected(taxFeeList(type(), format((new Date()), 'yyyy-MM-dd'), settingsForCompany?.subject_tax, settingsForCompany?.tax_fees ?? []), taxCode.value, companyForUser.language) : lang.labels.selectOption}
                  </>
                }
                { variant && setVariant &&
                  <>
                    { options.type === "sale" &&
                      <>
                        {variant.valid.tax_sale ? taxFeeSelected(taxFeeList(type(), format((new Date()), 'yyyy-MM-dd'), settingsForCompany?.subject_tax, settingsForCompany?.tax_fees ?? []), variant.value.tax_code_sale, companyForUser.language) : lang.labels.selectOption}
                      </>
                    }
                    { options.type === "purchase" &&
                      <>
                        {variant.valid.tax_purchase ? taxFeeSelected(taxFeeList(type(), format((new Date()), 'yyyy-MM-dd'), settingsForCompany?.subject_tax, settingsForCompany?.tax_fees ?? []), variant.value.tax_code_purchase, companyForUser.language) : lang.labels.selectOption}
                      </>
                    }
                  </>
                }
                </span>
            </span>
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-results">
              <ul className="select2-results__options" role="listbox">
                <li className={`select2-results__option select2-results__option--selectable ${(settingsForCompany?.tax_fees ?? []).length > 0 && "d-none"}`}>
                  <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                </li>
                { taxCode && taxRate && setTaxCode && setTaxRate && taxFeeList(type(), format((new Date()), 'yyyy-MM-dd'), settingsForCompany?.subject_tax, settingsForCompany?.tax_fees ?? []).map (( (item, other_index) => { return (
                  <li key={other_index} className={`select2-results__option select2-results__option--selectable ${item.code === taxCode.value && "select2-results__option--selected"}`} role="option" aria-selected={item.code === taxCode.value}>
                    <Dropdown.Item bsPrefix="select2-results__option__text py-2 text-capitalize" onClick={() => handleChangeTax(item)}>{item.label[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</Dropdown.Item>
                  </li>
                )}))}
                { variant && setVariant && taxFeeList(type(), format((new Date()), 'yyyy-MM-dd'), settingsForCompany?.subject_tax, settingsForCompany?.tax_fees ?? []).map (( (item, other_index) => { return (
                  <li key={other_index} className={`select2-results__option select2-results__option--selectable ${item.code === (options.type === "sale" ? variant.value.tax_code_sale : variant.value.tax_code_purchase)  && "select2-results__option--selected"}`} role="option" aria-selected={item.code === (options.type === "sale" ? variant.value.tax_code_sale : variant.value.tax_code_purchase)}>
                    <Dropdown.Item bsPrefix="select2-results__option__text py-2 text-capitalize" onClick={() => handleChangeTaxToVariant(item)}>{item.label[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</Dropdown.Item>
                  </li>
                )}))}
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      { taxCode && taxRate && setTaxCode && setTaxRate &&
        <AppLegend component={component} attribute={{check: taxCode.valid, name: `tax-${options.type}`, index: options.index, sub_index: options.sub_index}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
      }
      { variant && setVariant &&
        <AppLegend component={component} attribute={{check: options.type === "sale" ? variant.valid.tax_sale : variant.valid.tax_purchase, name: `variant-tax-${options.type}`, index: options.index, sub_index: options.sub_index}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
      }
    </>
  )
}

export default AppInputTaxFee;
